import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Users, RotateCcw, Trophy, X, Clock, Flag, Eye, Copy, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { AuthContext, WS_URL, API } from "../App";
import axios from "axios";

// Chess piece Unicode characters
const PIECES = {
  'K': '♔', 'Q': '♕', 'R': '♖', 'B': '♗', 'N': '♘', 'P': '♙',
  'k': '♚', 'q': '♛', 'r': '♜', 'b': '♝', 'n': '♞', 'p': '♟'
};

// Initial board setup
const INITIAL_BOARD = [
  ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'],
  ['p', 'p', 'p', 'p', 'p', 'p', 'p', 'p'],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  ['P', 'P', 'P', 'P', 'P', 'P', 'P', 'P'],
  ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R']
];

const ChessGame = ({ onClose, currentUser, user, token: propToken, friends = [] }) => {
  const { token: authToken, user: authUser } = useContext(AuthContext);
  const effectiveToken = propToken || authToken;
  const username = currentUser?.username || user?.username || authUser?.username || "Player";
  
  const [view, setView] = useState('lobby'); // lobby, playing, spectate
  const [isSpectator, setIsSpectator] = useState(false);
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [currentTable, setCurrentTable] = useState(null);
  const [board, setBoard] = useState(JSON.parse(JSON.stringify(INITIAL_BOARD)));
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [currentTurn, setCurrentTurn] = useState('white'); // white or black
  const [myColor, setMyColor] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [gameStatus, setGameStatus] = useState('waiting'); // waiting, playing, checkmate, stalemate, draw
  const [inCheck, setInCheck] = useState(false);
  const [moveHistory, setMoveHistory] = useState([]);
  const [capturedPieces, setCapturedPieces] = useState({ white: [], black: [] });
  const [message, setMessage] = useState("");
  const wsRef = useRef(null);

  // Check if piece belongs to color
  const isPieceColor = (piece, color) => {
    if (!piece) return false;
    return color === 'white' ? piece === piece.toUpperCase() : piece === piece.toLowerCase();
  };

  // Get valid moves for a piece (simplified - real chess has more complex rules)
  const getValidMoves = useCallback((row, col, boardState) => {
    const piece = boardState[row][col];
    if (!piece) return [];
    
    const isWhite = piece === piece.toUpperCase();
    const moves = [];
    const pieceType = piece.toLowerCase();

    // Helper to check if move is valid (not capturing own piece)
    const canMoveTo = (r, c) => {
      if (r < 0 || r > 7 || c < 0 || c > 7) return false;
      const target = boardState[r][c];
      if (!target) return true;
      return isWhite ? target === target.toLowerCase() : target === target.toUpperCase();
    };

    // Helper to check if square has enemy piece
    const hasEnemy = (r, c) => {
      if (r < 0 || r > 7 || c < 0 || c > 7) return false;
      const target = boardState[r][c];
      if (!target) return false;
      return isWhite ? target === target.toLowerCase() : target === target.toUpperCase();
    };

    switch (pieceType) {
      case 'p': // Pawn
        const direction = isWhite ? -1 : 1;
        const startRow = isWhite ? 6 : 1;
        // Forward move
        if (!boardState[row + direction]?.[col]) {
          moves.push([row + direction, col]);
          // Double move from start
          if (row === startRow && !boardState[row + 2 * direction]?.[col]) {
            moves.push([row + 2 * direction, col]);
          }
        }
        // Captures
        if (hasEnemy(row + direction, col - 1)) moves.push([row + direction, col - 1]);
        if (hasEnemy(row + direction, col + 1)) moves.push([row + direction, col + 1]);
        break;

      case 'r': // Rook
        for (let i = row + 1; i <= 7; i++) { if (!boardState[i][col]) moves.push([i, col]); else { if (hasEnemy(i, col)) moves.push([i, col]); break; } }
        for (let i = row - 1; i >= 0; i--) { if (!boardState[i][col]) moves.push([i, col]); else { if (hasEnemy(i, col)) moves.push([i, col]); break; } }
        for (let j = col + 1; j <= 7; j++) { if (!boardState[row][j]) moves.push([row, j]); else { if (hasEnemy(row, j)) moves.push([row, j]); break; } }
        for (let j = col - 1; j >= 0; j--) { if (!boardState[row][j]) moves.push([row, j]); else { if (hasEnemy(row, j)) moves.push([row, j]); break; } }
        break;

      case 'n': // Knight
        const knightMoves = [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]];
        knightMoves.forEach(([dr, dc]) => { if (canMoveTo(row+dr, col+dc)) moves.push([row+dr, col+dc]); });
        break;

      case 'b': // Bishop
        for (let i = 1; i <= 7; i++) { if (row+i > 7 || col+i > 7) break; if (!boardState[row+i][col+i]) moves.push([row+i, col+i]); else { if (hasEnemy(row+i, col+i)) moves.push([row+i, col+i]); break; } }
        for (let i = 1; i <= 7; i++) { if (row+i > 7 || col-i < 0) break; if (!boardState[row+i][col-i]) moves.push([row+i, col-i]); else { if (hasEnemy(row+i, col-i)) moves.push([row+i, col-i]); break; } }
        for (let i = 1; i <= 7; i++) { if (row-i < 0 || col+i > 7) break; if (!boardState[row-i][col+i]) moves.push([row-i, col+i]); else { if (hasEnemy(row-i, col+i)) moves.push([row-i, col+i]); break; } }
        for (let i = 1; i <= 7; i++) { if (row-i < 0 || col-i < 0) break; if (!boardState[row-i][col-i]) moves.push([row-i, col-i]); else { if (hasEnemy(row-i, col-i)) moves.push([row-i, col-i]); break; } }
        break;

      case 'q': // Queen (Rook + Bishop)
        // Rook moves
        for (let i = row + 1; i <= 7; i++) { if (!boardState[i][col]) moves.push([i, col]); else { if (hasEnemy(i, col)) moves.push([i, col]); break; } }
        for (let i = row - 1; i >= 0; i--) { if (!boardState[i][col]) moves.push([i, col]); else { if (hasEnemy(i, col)) moves.push([i, col]); break; } }
        for (let j = col + 1; j <= 7; j++) { if (!boardState[row][j]) moves.push([row, j]); else { if (hasEnemy(row, j)) moves.push([row, j]); break; } }
        for (let j = col - 1; j >= 0; j--) { if (!boardState[row][j]) moves.push([row, j]); else { if (hasEnemy(row, j)) moves.push([row, j]); break; } }
        // Bishop moves
        for (let i = 1; i <= 7; i++) { if (row+i > 7 || col+i > 7) break; if (!boardState[row+i][col+i]) moves.push([row+i, col+i]); else { if (hasEnemy(row+i, col+i)) moves.push([row+i, col+i]); break; } }
        for (let i = 1; i <= 7; i++) { if (row+i > 7 || col-i < 0) break; if (!boardState[row+i][col-i]) moves.push([row+i, col-i]); else { if (hasEnemy(row+i, col-i)) moves.push([row+i, col-i]); break; } }
        for (let i = 1; i <= 7; i++) { if (row-i < 0 || col+i > 7) break; if (!boardState[row-i][col+i]) moves.push([row-i, col+i]); else { if (hasEnemy(row-i, col+i)) moves.push([row-i, col+i]); break; } }
        for (let i = 1; i <= 7; i++) { if (row-i < 0 || col-i < 0) break; if (!boardState[row-i][col-i]) moves.push([row-i, col-i]); else { if (hasEnemy(row-i, col-i)) moves.push([row-i, col-i]); break; } }
        break;

      case 'k': // King
        const kingMoves = [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]];
        kingMoves.forEach(([dr, dc]) => { if (canMoveTo(row+dr, col+dc)) moves.push([row+dr, col+dc]); });
        break;
    }

    return moves;
  }, []);

  // Handle square click
  const handleSquareClick = (row, col) => {
    if (gameStatus !== 'playing') return;
    if (currentTurn !== myColor) return;

    const piece = board[row][col];

    if (selectedSquare) {
      // Try to make a move
      const isValidMove = validMoves.some(([r, c]) => r === row && c === col);
      if (isValidMove) {
        makeMove(selectedSquare[0], selectedSquare[1], row, col);
      }
      setSelectedSquare(null);
      setValidMoves([]);
    } else if (piece && isPieceColor(piece, myColor)) {
      // Select piece
      setSelectedSquare([row, col]);
      setValidMoves(getValidMoves(row, col, board));
    }
  };

  // Make a move
  const makeMove = (fromRow, fromCol, toRow, toCol) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'move',
        from: [fromRow, fromCol],
        to: [toRow, toCol]
      }));
    }
  };

  // WebSocket connection
  const connectWs = useCallback((tableCode, color) => {
    if (wsRef.current) wsRef.current.close();
    
    const ws = new WebSocket(`${WS_URL}/ws/chess/${tableCode}?token=${effectiveToken}&color=${color}`);
    
    ws.onopen = () => {
      console.log("Chess WS connected");
      setMessage("Waiting for opponent...");
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'game_start':
          setOpponent(data.opponent);
          setGameStatus('playing');
          setMessage("");
          break;
        case 'game_state':
          setBoard(data.board);
          setCurrentTurn(data.current_turn);
          setInCheck(data.in_check);
          setMoveHistory(data.move_history || []);
          setCapturedPieces(data.captured || { white: [], black: [] });
          break;
        case 'move':
          setBoard(data.board);
          setCurrentTurn(data.current_turn);
          setInCheck(data.in_check);
          if (data.captured_piece) {
            setCapturedPieces(prev => ({
              ...prev,
              [data.captured_by]: [...prev[data.captured_by], data.captured_piece]
            }));
          }
          break;
        case 'game_over':
          setGameStatus(data.result);
          toast.success(data.message);
          break;
        case 'opponent_left':
          toast.info("Opponent left the game");
          setGameStatus('waiting');
          setOpponent(null);
          break;
        case 'error':
          toast.error(data.message);
          break;
      }
    };
    
    ws.onclose = () => {
      console.log("Chess WS closed");
    };
    
    wsRef.current = ws;
  }, [effectiveToken]);

  // Create game
  const createGame = (color) => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setCurrentTable({ code });
    setMyColor(color);
    connectWs(code, color);
    setView('game');
    setBoard(JSON.parse(JSON.stringify(INITIAL_BOARD)));
    setGameStatus('waiting');
  };

  // Join game
  const joinGame = (code) => {
    setCurrentTable({ code });
    setMyColor('black'); // Joiner plays black
    connectWs(code, 'black');
    setView('game');
    setBoard(JSON.parse(JSON.stringify(INITIAL_BOARD)));
  };

  // Leave game
  const leaveGame = () => {
    if (wsRef.current) wsRef.current.close();
    setView('lobby');
    setCurrentTable(null);
    setMyColor(null);
    setOpponent(null);
    setBoard(JSON.parse(JSON.stringify(INITIAL_BOARD)));
    setGameStatus('waiting');
  };

  // Resign
  const resign = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'resign' }));
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  if (view === 'lobby') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-amber-900 to-amber-950 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              ♔ CHESS
            </h2>
            <button onClick={onClose} className="text-white hover:text-red-400">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => createGame('white')}
              className="bg-white text-black py-4 rounded-lg font-bold hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
            >
              ♔ PLAY AS WHITE
            </button>
            <button
              onClick={() => createGame('black')}
              className="bg-gray-900 text-white py-4 rounded-lg font-bold hover:bg-gray-800 transition-all border-2 border-white flex items-center justify-center gap-2"
            >
              ♚ PLAY AS BLACK
            </button>
          </div>

          <div className="bg-black/20 rounded-lg p-4 mb-4">
            <h3 className="text-white font-bold mb-2">JOIN GAME</h3>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Enter game code..."
                className="flex-1 px-4 py-2 rounded bg-black/30 text-white border border-amber-600/50 uppercase"
                id="chess-code-input"
              />
              <button
                onClick={() => {
                  const code = document.getElementById('chess-code-input').value;
                  if (code) joinGame(code.toUpperCase());
                }}
                className="bg-amber-600 text-white px-6 py-2 rounded font-bold hover:bg-amber-500"
              >
                JOIN
              </button>
            </div>
            <button
              onClick={() => {
                const code = document.getElementById('chess-code-input').value;
                if (code) {
                  setCurrentTable(code.toUpperCase());
                  setIsSpectator(true);
                  setView('spectate');
                  toast.success(`Watching game ${code.toUpperCase()}`);
                }
              }}
              className="w-full mt-2 bg-blue-600/50 text-white px-4 py-2 rounded font-medium hover:bg-blue-600 flex items-center justify-center gap-2"
            >
              <Eye className="w-4 h-4" /> SPECTATE GAME
            </button>
          </div>

          <div className="bg-black/20 rounded-lg p-4 text-gray-300 text-sm">
            <h3 className="font-bold text-amber-400 mb-2">HOW TO PLAY</h3>
            <p>Standard chess rules apply. Click a piece to see valid moves, then click destination to move.</p>
          </div>
        </div>
      </div>
    );
  }

  // Game View
  const displayBoard = myColor === 'black' ? [...board].reverse().map(row => [...row].reverse()) : board;

  return (
    <div className="w-full h-full bg-gradient-to-b from-amber-900 to-amber-950 flex flex-col">
      {/* Header */}
      <div className="bg-black/40 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-white font-bold">Game: {currentTable?.code}</span>
          <span className={`px-2 py-1 rounded text-sm font-bold ${myColor === 'white' ? 'bg-white text-black' : 'bg-gray-900 text-white'}`}>
            You: {myColor?.toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {gameStatus === 'playing' && (
            <button onClick={resign} className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-500 flex items-center gap-1">
              <Flag className="w-4 h-4" /> Resign
            </button>
          )}
          <button onClick={leaveGame} className="text-red-400 hover:text-red-300 text-sm">
            Leave
          </button>
        </div>
      </div>

      {/* Game Area */}
      <div className="flex-1 flex items-center justify-center p-4 gap-6">
        {/* Board */}
        <div className="relative">
          {/* Opponent info */}
          <div className="text-center mb-2">
            <span className={`px-3 py-1 rounded ${myColor === 'white' ? 'bg-gray-900 text-white' : 'bg-white text-black'} font-bold`}>
              {opponent || "Waiting..."} {currentTurn !== myColor && gameStatus === 'playing' && '(thinking...)'}
            </span>
          </div>
          
          {/* Chess Board */}
          <div className="grid grid-cols-8 border-4 border-amber-800 shadow-2xl">
            {displayBoard.map((row, rowIndex) => (
              row.map((piece, colIndex) => {
                const actualRow = myColor === 'black' ? 7 - rowIndex : rowIndex;
                const actualCol = myColor === 'black' ? 7 - colIndex : colIndex;
                const isLight = (actualRow + actualCol) % 2 === 0;
                const isSelected = selectedSquare && selectedSquare[0] === actualRow && selectedSquare[1] === actualCol;
                const isValidMove = validMoves.some(([r, c]) => r === actualRow && c === actualCol);
                
                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    onClick={() => handleSquareClick(actualRow, actualCol)}
                    className={`w-12 h-12 flex items-center justify-center cursor-pointer transition-all
                      ${isLight ? 'bg-amber-200' : 'bg-amber-700'}
                      ${isSelected ? 'ring-4 ring-yellow-400' : ''}
                      ${isValidMove ? 'ring-4 ring-green-400' : ''}
                      hover:brightness-110
                    `}
                  >
                    {piece && (
                      <span className={`text-4xl ${piece === piece.toUpperCase() ? 'text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]' : 'text-black drop-shadow-[0_2px_2px_rgba(255,255,255,0.5)]'}`}>
                        {PIECES[piece]}
                      </span>
                    )}
                    {isValidMove && !piece && (
                      <div className="w-4 h-4 bg-green-500 rounded-full opacity-50" />
                    )}
                  </div>
                );
              })
            ))}
          </div>

          {/* Your info */}
          <div className="text-center mt-2">
            <span className={`px-3 py-1 rounded ${myColor === 'white' ? 'bg-white text-black' : 'bg-gray-900 text-white'} font-bold`}>
              {user?.username} (You) {currentTurn === myColor && gameStatus === 'playing' && '- Your turn!'}
            </span>
          </div>
        </div>

        {/* Side Panel */}
        <div className="w-48 bg-black/30 rounded-lg p-3">
          <h3 className="text-amber-400 font-bold mb-2 text-sm">STATUS</h3>
          <div className={`text-center py-2 rounded mb-3 ${
            gameStatus === 'waiting' ? 'bg-yellow-600' :
            gameStatus === 'playing' ? 'bg-green-600' : 'bg-red-600'
          } text-white font-bold text-sm`}>
            {gameStatus === 'waiting' ? 'Waiting...' :
             gameStatus === 'playing' ? (currentTurn === myColor ? 'YOUR TURN' : "OPPONENT'S TURN") :
             gameStatus.toUpperCase()}
          </div>
          
          {inCheck && (
            <div className="bg-red-600 text-white text-center py-1 rounded mb-3 text-sm font-bold animate-pulse">
              CHECK!
            </div>
          )}

          {/* Captured Pieces */}
          <div className="mb-3">
            <div className="text-xs text-gray-400 mb-1">Captured by you:</div>
            <div className="text-2xl">
              {capturedPieces[myColor]?.map((p, i) => <span key={i}>{PIECES[p]}</span>)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-1">Captured by opponent:</div>
            <div className="text-2xl">
              {capturedPieces[myColor === 'white' ? 'black' : 'white']?.map((p, i) => <span key={i}>{PIECES[p]}</span>)}
            </div>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className="bg-amber-600 text-black p-2 text-center font-bold">
          {message}
        </div>
      )}
    </div>
  );
};

export default ChessGame;
